import * as React from 'react'
import { styled } from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faVolume } from '@fortawesome/pro-solid-svg-icons'
import { b, RD, s, M } from '@packages/prelude'
import * as domain from '@packages/domain'
import { ControlButton as _ControlButton } from '~/components/Buttons'
import { useElevenLabsTTS } from '~/hooks/useElevenLabsTTS'

const ControlButton = styled(_ControlButton)`
  margin-top: 0 !important;
`

interface Props {
  id: string
  label: string
  description?: string | null
  value: string
  disabled: boolean
  onChange: (value: string) => void
  config: domain.Api.Config.Type
  ttsOptions: domain.Api.TTS.Type
}

const Container = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
`

const Input = styled.input.attrs({ type: 'text' })`
  flex: 1;
  min-width: 200px;
  border-radius: 1.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #ccc;
  font-family: 'Source Sans 3';
  font-size: 1.25rem;
  color: var(--primary);
`

const isValidName = (name: string) =>
  M.concatAll(b.MonoidAll)([
    s.trim(name).length > 1,
    s.test(/^[a-zA-ZäöüÄÖÜßàáâãäåæçèéêëìíîïðñòóôõöøùúûüýþÿÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝÞŸ-\s]+$/)(name)
  ])

export const View: React.FC<Props> = ({
  id,
  label,
  description: _description,
  value,
  disabled = false,
  onChange,
  ttsOptions
}) => {
  const { audio, generateAudio } = useElevenLabsTTS()

  React.useEffect(() => {
    if (RD.isSuccess(audio)) {
      const url = URL.createObjectURL(audio.value)
      const el = new Audio(url)
      const revoke = () => URL.revokeObjectURL(url)
      el.addEventListener('ended', revoke, { once: true })
      void el.play().finally(() => {
        // Safety revoke if ended didn't fire
        setTimeout(revoke, 10_000)
      })
    }
  }, [audio])

  const onPronounce = React.useCallback(() => {
    const text = ttsOptions.tts?.pronunciation_template?.replaceAll('%name%', value)

    s.isString(text) && generateAudio(text)()
  }, [value, generateAudio, ttsOptions])

  return (
    <Container>
      <Input id={id} aria-label={label} placeholder={label} value={value} onChange={e => onChange(e.target.value)} />
      <ControlButton
        onClick={onPronounce}
        disabled={disabled || !isValidName(value) || RD.isPending(audio)}
        aria-label="Aussprache abspielen">
        <FontAwesomeIcon icon={faVolume} />
      </ControlButton>
    </Container>
  )
}
